<template>
  <div class="small-12 medium-4 bg-white campaign--panel">
    <div class="campaign--info">
      <div class="campaign-info--header">
        <woot-button
          :icon="closeIconName"
          class="clear secondary close-button--rtl"
          @click="onPanelToggle"
        />
      </div>

      <div class="campaign--details">
        <div class="campaign--name-wrap">
          <h3 class="sub-block-title campaign--name">
            {{ campaign.title }}
          </h3>
        </div>

        <div class="campaign--content-wrap">
          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.SENDER') }}:
            </h3>

            <UserAvatarWithName :user="campaign.sender" />
          </div>

          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.INBOX') }}:
            </h3>

            <InboxName :inbox="campaign.inbox" />
          </div>

          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.CREATED_AT') }}:
            </h3>

            <h3 class="sub-block-subtitle">
              {{
                messageStamp(generateTimestamp(campaign), 'dd/MM/yy - HH:mm')
              }}
            </h3>
          </div>

          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.SCHEDULED_AT') }}:
            </h3>

            <h3 class="sub-block-subtitle">
              {{
                messageStamp(
                  new Date(campaign.scheduled_at),
                  'dd/MM/yy - HH:mm'
                )
              }}
            </h3>
          </div>

          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.STATUS') }}:
            </h3>

            <Label
              :title="getInfoLabel(campaign).title"
              :colorScheme="getInfoLabel(campaign).color"
            />
          </div>

          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{
                $t(
                  'CAMPAIGN.LIST.TABLE_HEADER.ACTION_TO_OPEN_CONVERSATION'
                )
              }}:
            </h3>
            <h3 class="sub-block-subtitle">
              {{
                $t(
                  `CAMPAIGN.LIST.ACTION_TO_OPEN_CONVERSATION.${campaign.action_to_open_conversation.toUpperCase()}`
                )
              }}
            </h3>            
          </div>

          <div class="campaign--content-wrap-item">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.PUBLIC') }}:
            </h3>

            <CampaignPublic v-if="campaign.audience_tags" :labels="campaign.audience_tags" />
          </div>

          <div class="campaign--content-wrap-item" v-if="campaign.file">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.ATTACHMENT') }}:
            </h3>

            <a :href="campaign.file.file_url" target="_blank">
              Visualizar {{ getFileType(campaign) }}
            </a>
          </div>

          <div class="campaign--content-wrap-item" v-if="campaign.team">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.TEAM') }}:
            </h3>

            <h3 class="sub-block-subtitle">
              {{ campaign.team.name }}
            </h3>
          </div>

          <div class="campaign--content-wrap-item" v-if="campaign.tag_name">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.LABEL') }}:
            </h3>

            <CampaignPublic :labels="getCampaignLabel(campaign)" />
          </div>

          <div class="campaign--content-wrap-item" v-if="templateName">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.TEMPLATE_NAME') }}:
            </h3>

            <h3 class="sub-block-subtitle">
              {{ templateName }}
            </h3>
          </div>

          <div class="campaign--content-wrap-item-custom">
            <h3 class="sub-block-title">
              {{ $t('CAMPAIGN.LIST.TABLE_HEADER.TEMPLATE') }}:
            </h3>

            <p v-html="formatMessage(campaign.message)"></p>
          </div>

          <div class="campaign--content-wrap-item">
            <woot-button
              v-if="isAdmin"
              type="button"
              :disabled="campaign.campaign_status === 'completed'"
              :is-loading="uiFlags.isDeleting"
              @click="toggleDeleteModal"
            >
              {{ $t('CAMPAIGN.LIST.BUTTONS.DELETE') }}
            </woot-button>
          </div>
        </div>
      </div>

      <woot-delete-modal
        v-if="showDeleteModal"
        :show.sync="showDeleteModal"
        :on-close="closeDelete"
        :on-confirm="confirmDeletion"
        :title="$t('CAMPAIGN.DELETE.CONFIRM.TITLE')"
        :message="$t('CAMPAIGN.DELETE.CONFIRM.MESSAGE')"
        :confirm-text="$t('CAMPAIGN.DELETE.CONFIRM.YES')"
        :reject-text="$t('CAMPAIGN.DELETE.CONFIRM.NO')"
        :is-loading="uiFlags.isDeleting"
      />
    </div>
  </div>
</template>

<script>
import Label from 'dashboard/components/ui/Label';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName';
import InboxName from 'dashboard/components/widgets/InboxName';
import CampaignPublic from './CampaignPublic';

import adminMixin from 'dashboard/mixins/isAdmin';
import timeMixin from 'dashboard/mixins/time';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import alertMixin from 'shared/mixins/alertMixin';

import { mapGetters } from 'vuex';
import LabelsAPI from '../../../../../api/labels.js';

export default {
  components: {
    InboxName,
    Label,
    UserAvatarWithName,
    CampaignPublic,
  },
  mixins: [adminMixin, alertMixin, messageFormatterMixin, timeMixin],
  props: {
    campaign: {
      type: Object,
      default: () => ({}),
    },
    closeIconName: {
      type: String,
      default: 'dismiss',
    },
  },
  data() {
    return {
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',
      labelList: 'labels/getLabels',
    }),
    typeFile() {
      return {
        document: ['application/pdf', 'application/msword'],
        image: ['image/png', 'image/jpeg'],
        video: ['video/mp4', 'video/3gpp'],
      };
    },
    templateName() {
      return this.campaign?.trigger_rules?.template_params?.name || null;
    },
  },
  watch: {
    'campaign.id': {
      immediate: true,
      handler() {
        this.getAudienceTags();
      },
    },
  },
  methods: {
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    getPublic({ audience }, labelList = this.labelList) {
      if (!audience || !Array.isArray(audience)) {
        return [];
      }

      const ids = audience.map(({ id }) => id);

      return labelList
        .filter(item => ids.includes(item.id))
        .map(({ title, color }) => {
          return { title, color };
        });
    },
    getCampaignLabel({ tag_id, tag_name }) {
      if (!tag_id || !tag_name) {
        return [];
      }

      const matchedLabel = this.labelList.find(label => label.id === tag_id);

      if (matchedLabel) {
        return [
          {
            title: matchedLabel.title,
            color: matchedLabel.color,
          },
        ];
      }

      return [
        {
          title: tag_name,
          color: '#1f93ff',
        },
      ];
    },
    getInfoLabel({ campaign_status }) {
      const title =
        campaign_status === 'completed'
          ? this.$t('CAMPAIGN.LIST.STATUS.COMPLETED')
          : this.$t('CAMPAIGN.LIST.STATUS.ACTIVE');

      const color = campaign_status === 'completed' ? 'secondary' : 'success';

      return { title, color };
    },    
    generateTimestamp({ created_at }) {
      const time = new Date(created_at).getTime() / 1000;
      return Math.floor(time);
    },
    onPanelToggle() {
      this.$emit('toggle-panel');
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('campaigns/delete', this.campaign.id);
        this.$emit('load-campaign');
        this.showAlert(this.$t('CAMPAIGN.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('CAMPAIGN.DELETE.API.ERROR_MESSAGE')
        );
      }
    },    
    closeDelete() {
      this.showDeleteModal = false;
    },
    getFileType(campaign) {
      const { file_type } = campaign.file;

      if (this.typeFile.document.includes(file_type)) return 'documento';
      if (this.typeFile.image.includes(file_type)) return 'imagem';
      if (this.typeFile.video.includes(file_type)) return 'video';

      return 'arquivo';
    },
    getAudienceTags(){
      if(this.campaign.audience_type == 'label' && this.campaign.audience.length > 0 && this.campaign.audience_tags === undefined){
        let labels = this.getPublic(this.campaign);
        if(labels.length == 0){
          LabelsAPI.getByAccount(this.campaign.account.id).then((response) => {
            const labelList = response.data.payload ? response.data.payload : response.data;
            this.setAudienceTags(this.getPublic(this.campaign, labelList));            
          }).catch((error) => {
            this.setAudienceTags([]);            
          });
        }else{
          this.setAudienceTags(labels);          
        }      
      }
    },
    setAudienceTags(labels){
      this.$store.dispatch('campaigns/editValueCampaign', {
        ...this.campaign, audience_tags: labels,
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.campaign--panel {
  height: 100%;
  background: white;
  font-size: var(--font-size-small);
  overflow-y: auto;
  overflow: auto;
  position: relative;
  border-right: 1px solid var(--color-border);

  .campaign--info {
    text-align: left;

    .campaign-info--header {
      background-color: #053ca8;
      height: var(--space-larger);

      .close-button--rtl {
        top: var(--space-micro);
        right: var(--space-micro);
        position: absolute;
        color: var(--white);

        &:hover {
          background-color: transparent;
          color: var(--white-transparent);
        }
      }
    }
  }

  .campaign--details {
    width: 100%;
    padding: var(--space-normal);

    .campaign--name-wrap {
      margin-bottom: var(--space-small);
    }

    .campaign--name {
      text-transform: capitalize;
      white-space: normal;
      font-size: var(--font-size-large);
    }

    .campaign--content-wrap {
      margin-top: var(--space-small);

      .campaign--content-wrap-item {
        display: flex;
        align-items: center;
        margin-top: var(--space-medium);

        .sub-block-title {
          margin-right: var(--space-one);
          font-size: var(--font-size-small);
        }

        .sub-block-subtitle {
          font-size: var(--font-size-mini);
        }

        .row--user-block {
          align-items: center;
          display: flex;
          text-align: left;

          .user-name {
            margin: 0 var(--space-small);
          }
        }

        .inbox--name {
          margin-top: var(--space-minus-normal);
        }

        button {
          width: 100%;
        }
      }

      .campaign--content-wrap-item-custom {
        margin-top: var(--space-medium);

        .sub-block-title {
          margin-right: var(--space-one);
          font-size: var(--font-size-small);
        }
      }
    }
  }
}
</style>
